import { Network } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { apiErrorMessage, setAggregatorToken } from '../../api/client'
import { aggregatorPortalApi } from '../../api/endpoints'
import { ErrorMessage } from '../../components/ErrorMessage'
import { Logo } from '../../components/Logo'
import type { AggregatorAudience, CoordinatorInviteView } from '../../types/api'
import { isValidMobile, MOBILE_PATTERN } from '../../utils/validation'

const AUDIENCES: { value: AggregatorAudience; label: string; hint: string }[] = [
  { value: 'STUDENTS', label: 'Seekers', hint: 'You bring in learners (students)' },
  { value: 'TUTORS', label: 'Providers', hint: 'You bring in tutors / trainers' },
]

/**
 * A coordinator accepting an organisation's invitation - the sign-up form from the invitation link.
 *
 * <p>"Aggregator Type" decides the coordinator's whole dashboard: a Seekers coordinator uploads and
 * links learners, a Providers coordinator tutors. The email is the invitation's and is not asked for.
 */
export function CoordinatorInvitePage() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const [invite, setInvite] = useState<CoordinatorInviteView | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [audience, setAudience] = useState<AggregatorAudience>('STUDENTS')
  const [subOrgName, setSubOrgName] = useState('')
  const [website, setWebsite] = useState('')
  const [contactName, setContactName] = useState('')
  const [phone, setPhone] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    aggregatorPortalApi
      .viewInvite(token)
      .then((view) => {
        setInvite(view)
        if (view.contactName) setContactName(view.contactName)
      })
      .catch((err) => setLoadError(apiErrorMessage(err, 'This invitation link is not valid')))
  }, [token])

  function validate(): string | null {
    if (!subOrgName.trim()) return 'Enter your sub-org, division, vertical or institution name'
    if (!contactName.trim()) return 'Enter the contact name'
    if (!isValidMobile(phone)) return 'Enter a ten-digit mobile number'
    if (!termsAccepted) return 'Accept the terms to finish'
    return null
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    const problem = validate()
    if (problem) {
      setError(problem)
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await aggregatorPortalApi.acceptInvite(token, {
        audience,
        subOrgName: subOrgName.trim(),
        website: website.trim() || undefined,
        contactName: contactName.trim(),
        phone,
        termsAccepted,
      })
      setAggregatorToken(res.token, res.accountType)
      navigate('/aggregator-portal', { replace: true })
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not complete the sign-up'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-sun-hero px-4 py-6">
      <div className="w-full max-w-xl">
        <div className="text-center">
          <Link to="/" aria-label="YellowDots home" className="inline-block">
            <Logo size="page" />
          </Link>
          <h1 className="mt-4 flex items-center justify-center gap-2 text-3xl font-extrabold tracking-tight text-ink-900">
            <Network size={24} className="shrink-0 text-sun-600" />
            Coordinator sign-up
          </h1>
          {invite && (
            <p className="mt-1 text-ink-600">
              <span className="font-semibold">{invite.orgName}</span> has invited you to coordinate on YellowDots.
            </p>
          )}
        </div>

        <div className="card mt-5 p-5 shadow-float sm:p-6">
          {loadError ? (
            <div className="space-y-3">
              <ErrorMessage message={loadError} />
              <p className="text-sm text-ink-500">
                Already signed up?{' '}
                <Link to="/aggregator-portal/login" className="font-semibold text-ink-900 hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          ) : !invite ? (
            <p className="text-sm text-ink-500">Loading your invitation...</p>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <fieldset>
                <legend className="label label-required">Aggregator Type</legend>
                <div className="grid gap-2 sm:grid-cols-2">
                  {AUDIENCES.map((a) => (
                    <label
                      key={a.value}
                      className={`flex cursor-pointer items-start gap-2 rounded-lg border p-3 text-sm transition ${
                        audience === a.value ? 'border-sun-500 bg-sun-400/10' : 'border-ink-200 hover:border-ink-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="audience"
                        className="mt-1"
                        checked={audience === a.value}
                        onChange={() => setAudience(a.value)}
                      />
                      <span>
                        <span className="block font-bold text-ink-900">{a.label}</span>
                        <span className="text-ink-500">{a.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <label className="block">
                <span className="label label-required">Sub-Org / Division / Vertical / Institution Name</span>
                <input className="input" value={subOrgName} onChange={(e) => setSubOrgName(e.target.value)} placeholder="e.g. GarvSe Centre, Ghaziabad" />
                <span className="mt-1 block text-xs text-ink-400">
                  Any specific sub-identity needed for identification and tracking. May be the same as the organisation name.
                </span>
              </label>

              <label className="block">
                <span className="label">Website</span>
                <input className="input" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://yourorg.in" />
              </label>

              <h2 className="border-t border-ink-200 pt-4 font-bold text-ink-900">Primary Contact</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="label label-required">Contact Name</span>
                  <input className="input" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Full name of the primary contact" />
                </label>
                <label className="block">
                  <span className="label label-required">Phone</span>
                  <input
                    className="input"
                    inputMode="numeric"
                    maxLength={10}
                    pattern={MOBILE_PATTERN}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                    placeholder="98765 43210"
                  />
                </label>
              </div>
              <label className="block">
                <span className="label label-required">Email</span>
                <input className="input bg-ink-50" value={invite.email} readOnly />
                <span className="mt-1 block text-xs text-ink-400">
                  The address you were invited at - it is your login. Next time, you sign in with a join code sent
                  to this email.
                </span>
              </label>

              <label className="flex items-start gap-2 pt-1 text-sm text-ink-700">
                <input type="checkbox" className="mt-1" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
                <span>
                  I accept the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-dot-700 underline underline-offset-2">
                    Terms &amp; Conditions
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-dot-700 underline underline-offset-2">
                    Privacy Policy
                  </a>
                  , and will upload only people who have consented (a parent, for anyone under 18).
                </span>
              </label>

              <ErrorMessage message={error} />
              <button type="submit" disabled={submitting} className="btn-sun w-full">
                {submitting ? 'Creating...' : 'Create coordinator account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
